import { Menu } from '@mantine/core'
import type { Practitioner, Reference } from '@medplum/fhirtypes'
import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import { referenceDisplay } from '@seen/core/display'
import { idtRoles } from '@seen/core/models/care-teams/constants'
import type { SignatureTask } from '@seen/core/models/tasks/signature/signatureTask'
import { ROLES } from '@seen/core/roles'
import { formatReference } from '@seen/core/utils'
import { IconX } from '@tabler/icons-react'
import { type JSX, useMemo } from 'react'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { ReactTable } from '#src/components/fhir-table/ReactTable.tsx'
import { ReactTableDeleteAction } from '#src/components/fhir-table/ReactTableDeleteAction.tsx'
import { MantineMenuItemMedplumLinkAction } from '#src/components/fhir-table/ReactTableMedplumLinkAction.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import {
  SignatureButton,
  SignatureStatus,
} from '#src/components/signature/SignatureStatus.tsx'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

export function SignatureTable({
  signatures,
  dateFormat,
  showDiscipline = false,
}: {
  signatures: SignatureTask[]
  dateFormat?: string
  showDiscipline?: boolean
}): JSX.Element {
  const showAdminActions = usePortalStore((state) => state.showAdminActions)

  const columnConfigs = useMemo<ColumnConfig<SignatureTask>[]>(
    () => [
      ...(showDiscipline
        ? [
            {
              id: 'discipline',
              headerText: 'Discipline',
              size: 110,
              render: (signature: SignatureTask) => (
                <span>{roleLabel(signature) || '—'}</span>
              ),
              valueType: 'text' as const,
              // Sort by IDT-role order so the disciplines always read top-to-bottom
              // in the same sequence, making a missing one easy to spot.
              getSortFilterValue: (signature: SignatureTask) =>
                roleOrder(signature),
              filteringType: 'text' as const,
              enableSorting: true,
              useAsInitialSort: true,
            },
          ]
        : []),
      {
        id: 'name',
        headerText: 'Name',
        size: 120,
        render: (signature) =>
          signature.recipient ? (
            <PractitionerDisplay
              practitioner={signature.recipient as Reference<Practitioner>}
            />
          ) : (
            <span className="text-foreground-secondary italic">Unassigned</span>
          ),
        valueType: 'text',
        getSortFilterValue: (signature) => formatReference(signature.recipient),
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'dateSigned',
        headerText: 'Date Signed',
        size: 100,
        render: (signature) => (
          <DateTimeDisplay
            value={signature.completionDate}
            format={dateFormat}
          />
        ),
        valueType: 'date' as const,
        getSortFilterValue: (signature) => signature.completionDate,
        filteringType: 'date-range',
        enableSorting: true,
        useAsInitialSort: !showDiscipline,
        sortDescFirst: true,
      },
      {
        id: 'status',
        headerText: 'Status',
        render: (signature) => <SignatureStatus signature={signature} />,
        valueType: 'text',
        getSortFilterValue: (signature) => signature.state,
        filteringType: 'text',
        enableSorting: true,
      },
      {
        id: 'actions',
        headerText: '',
        size: 50,
        render: (signature) => (
          <MoreDropdownMenu modal={false}>
            {signature.state === 'todo' && (
              <DropdownMenuItem asChild>
                <SignatureButton
                  signature={signature}
                  label={`Sign on behalf of ${behalfOfTarget(signature)}`}
                  onSigned={() => {}}
                  withConfirmation
                  confirmationTitle="Sign on behalf"
                  confirmationDescription={`Are you sure you want to sign this on behalf of ${behalfOfTarget(signature)}?`}
                />
              </DropdownMenuItem>
            )}
            {signature.state !== 'todo' && (
              <DropdownMenuRemoveSignatureItem signature={signature} />
            )}
          </MoreDropdownMenu>
        ),
        valueType: 'text',
        enableSorting: false,
        getSortFilterValue: () => '',
      },
    ],
    [dateFormat, showDiscipline]
  )

  return (
    <ReactTable
      columnConfigs={columnConfigs}
      data={signatures}
      tableOptions={{
        enableColumnFilters: false,
        enableGlobalFilter: false,
        enablePagination: false,
        enableSorting: true,
        enableRowActions: showAdminActions,
        renderRowActionMenuItems: ({ row }) => (
          <>
            <Menu.Label>Admin</Menu.Label>
            <MantineMenuItemMedplumLinkAction
              resource={row.original.toFhirResource()}
            />
            <ReactTableDeleteAction row={row.original.toFhirResource()} />
          </>
        ),
      }}
    />
  )
}

function roleLabel(signature: SignatureTask): string {
  const role = signature.role
  return role ? (displayFromCodeableConcept(ROLES[role]) ?? role) : ''
}

// Position of the signature's role in the canonical IDT-role order, so the
// table can sort the roles into a stable top-to-bottom sequence. Returned as a
// zero-padded string because column sort values are text; tasks with no role
// sort last.
function roleOrder(signature: SignatureTask): string {
  const role = signature.role
  const order = role ? idtRoles.indexOf(role) : idtRoles.length
  return String(order).padStart(2, '0')
}

function behalfOfTarget(signature: SignatureTask): string {
  return (
    referenceDisplay(signature.recipient) ??
    referenceDisplay(signature.ownerPractitioner) ??
    (roleLabel(signature) || 'this discipline')
  )
}

function DropdownMenuRemoveSignatureItem({
  signature,
}: {
  signature: SignatureTask
}): JSX.Element {
  const patch = usePatchMutation<SignatureTask>()
  const { open: confirmWithAnnotation } = useConfirmWithAnnotationStore()
  return (
    <DropdownMenuItem
      onSelect={() => {
        confirmWithAnnotation({
          title: 'Remove signature?',
          description: 'Are you sure you want to remove this signature?',
          onConfirm: (note) => {
            patch.mutate({
              instance: signature,
              mutateFn: (s) => {
                s.removeSignature()
                s.description = note
              },
            })
          },
          isRichText: true,
        })
      }}
    >
      <IconX className="size-4 text-foreground-secondary" />
      Remove signature
    </DropdownMenuItem>
  )
}
